/**
 * Tiered performance mitigation for low-RAM / budget devices.
 *
 * Tier 1 (High Performance Mode): FPS stays under 45 for a continuous 2s ->
 *   `.high-perf-mode` on <body> + a dismissible notification bar.
 * Tier 2 (Low RAM Mode): FPS stays under 25 for another continuous 2s after
 *   tier 1 fires -> `.low-ram-mode` on <body>, wind/glow/sheen/card effects
 *   are switched off via window.RacingFX (see racing-theme.js), and the FPS
 *   loop stops itself. Tier 2 has its own "Turn off", separate from tier 1's.
 *   Its notification bar only slides in after the tier 1 bar has fully
 *   slid away — see PerfNotificationBar._swapTo — never an instant swap.
 *
 * Both tiers can also be flipped manually and independently from the
 * site-settings panel (header.liquid, [data-perf-toggle]) — checking "Low
 * RAM Mode" does NOT require "Performance Mode" to be checked too. The
 * checkboxes and the automated monitor share the same activate/deactivate
 * functions, so they always agree on state, and each choice persists across
 * visits via its own localStorage key.
 *
 * Each tier's automatic trigger only ever fires once per page load: if the
 * visitor turns a tier off (settings checkbox or the bar's "Turn off"), the
 * monitor retires that tier's auto-trigger for the rest of the session
 * instead of silently turning it back on the next time FPS dips. Manually
 * re-checking a box re-arms it. A full page reload also re-arms both.
 *
 * Note: the automated *monitor* still escalates tier1 -> tier2 sequentially
 * (it only starts watching for the <25fps/2s window after tier 1 has
 * already fired) — that sequencing is about how the monitor detects
 * degradation, independent of the settings panel decoupling above.
 *
 * The FPS trigger itself only ticks while this tab is the active, focused
 * one (see FPSMonitor#_isPageActive) — it pauses on tab-hide or window-blur
 * and resumes on visibilitychange/focus, so a backgrounded tab never racks
 * up a bogus low-fps reading or auto-activates a tier the visitor isn't
 * even watching.
 *
 * Load order: must come after racing-theme.js (defines window.RacingFX).
 */
(function () {
  'use strict';

  var STORAGE = {
    tier1: 'car77-perf-highperf',
    tier2: 'car77-perf-lowram'
  };

  var FX_CATEGORIES = ['wind', 'heat', 'sheens', 'cards'];

  var body = document.body;

  // --- FPS monitor -----------------------------------------------------

  function FPSMonitor(options) {
    this.tier1Threshold = options.tier1Threshold;
    this.tier2Threshold = options.tier2Threshold;
    this.tier1Delay = options.tier1Delay;
    this.tier2Delay = options.tier2Delay;
    this.onTier1 = options.onTier1;
    this.onTier2 = options.onTier2;
    this._tick = this._tick.bind(this);
    this.rafId = null;
    this.reset();
  }

  var SAMPLE_INTERVAL = 250; // ms — average fps over a real window, not per-frame.

  FPSMonitor.prototype.reset = function () {
    this.tier1Active = false;
    this.tier2Active = false;
    this.belowSince = null;
    this.frameCount = 0;
    this.sampleStart = null;
    this.wantsToRun = false;
  };

  // Only the active, focused tab should pay for the rAF loop — a backgrounded
  // or unfocused window either throttles rAF or keeps it at full rate while
  // the visitor isn't looking, and either way a fps reading taken then isn't
  // a real signal of this device struggling to render the page.
  FPSMonitor.prototype._isPageActive = function () {
    return document.visibilityState === 'visible' && document.hasFocus();
  };

  FPSMonitor.prototype.start = function () {
    this.wantsToRun = true;
    this._resume();
  };

  FPSMonitor.prototype.stop = function () {
    this.wantsToRun = false;
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = null;
  };

  // Restarts ticking after start() or after the tab regains visibility/focus.
  // No-op if already running, not wanted, or the tab still isn't active.
  FPSMonitor.prototype._resume = function () {
    if (this.rafId || !this.wantsToRun || !this._isPageActive()) return;
    this.frameCount = 0;
    this.sampleStart = performance.now();
    this.rafId = requestAnimationFrame(this._tick);
  };

  FPSMonitor.prototype._tick = function (now) {
    this.frameCount++;
    var elapsed = now - this.sampleStart;

    if (elapsed >= SAMPLE_INTERVAL) {
      // A windowed average absorbs a single "catch-up" frame after a CPU
      // stall (common under DevTools throttling) — that one fast frame
      // barely moves a count-over-real-time average, whereas it used to
      // spike a per-frame smoothed value back above threshold and silently
      // reset the continuous-low timer before it could reach 2s/2s.
      var fps = (this.frameCount / elapsed) * 1000;
      this.frameCount = 0;
      this.sampleStart = now;
      this._evaluate(fps, now);
    }

    // Tier 2 reached, or the tab is no longer the active/focused one: stop
    // rescheduling (a visibility/focus listener below calls _resume() to
    // pick tracking back up once the tab is active again — tier 2 stays off
    // for good since stop() already cleared wantsToRun).
    if (this.wantsToRun && this._isPageActive()) {
      this.rafId = requestAnimationFrame(this._tick);
    } else {
      this.rafId = null;
      if (!this._isPageActive()) this.belowSince = null; // don't count backgrounded/unfocused time as continuous low fps
    }
  };

  FPSMonitor.prototype._evaluate = function (fps, now) {
    // Below 45fps opens the tier 1 window; once tier 1 has fired, tier 2
    // requires the *harsher* sub-30fps condition, not just sub-45 again.
    var threshold = this.tier1Active ? this.tier2Threshold : this.tier1Threshold;

    if (fps >= threshold) {
      // Recovered above threshold: reset the continuous window that's
      // currently open. Once a tier has actually fired it stays active
      // until the user (or a "Turn off" link) reverts it.
      this.belowSince = null;
      return;
    }

    if (this.belowSince === null) this.belowSince = now;
    var elapsed = now - this.belowSince;

    if (!this.tier1Active) {
      if (elapsed >= this.tier1Delay) {
        this.tier1Active = true;
        this.belowSince = null; // Start a fresh continuous window for the tier 2 (<30fps) check.
        this.onTier1();
      }
    } else if (!this.tier2Active) {
      if (elapsed >= this.tier2Delay) {
        this.tier2Active = true;
        this.onTier2();
      }
    }
  };

  // --- Notification bar -------------------------------------------------

  function PerfNotificationBar() {
    this.el = document.createElement('div');
    this.el.className = 'perf-bar';
    this.el.setAttribute('role', 'status');
    this.el.setAttribute('aria-live', 'polite');
    this.el.hidden = true;
    document.body.appendChild(this.el);
    this._autoDismissId = null;
    this._transitionMs = 300; // Matches the CSS slide transition duration below.
  }

  var AUTO_DISMISS_MS = 4000; // Both tiers' bars stay up the same length of time.

  PerfNotificationBar.prototype.showTier1 = function (onTurnOff) {
    var self = this;
    this._swapTo(function () {
      self._render('Negative delta detected, enabling performance mode', onTurnOff);
      // Auto-dismisses the notification (not the mode itself) so it doesn't
      // sit on screen forever if the visitor ignores it — same duration as
      // the tier 2 bar below, so neither tier's popup lingers longer than
      // the other's.
      self._autoDismissId = window.setTimeout(function () {
        self.hide();
      }, AUTO_DISMISS_MS);
    });
  };

  PerfNotificationBar.prototype.showTier2 = function (onTurnOff) {
    var self = this;
    this._swapTo(function () {
      self._render('Low engine power detected, disabling amenities', onTurnOff);
      // Still auto-dismisses (just the notification, not the mode itself) so
      // it doesn't sit on screen forever if the visitor ignores it.
      self._autoDismissId = window.setTimeout(function () {
        self.hide();
      }, AUTO_DISMISS_MS);
    });
  };

  // Renders the message + "Turn off" action into the (currently hidden) bar
  // and slides it in.
  PerfNotificationBar.prototype._render = function (message, onTurnOff) {
    this.el.textContent = '';

    var text = document.createElement('span');
    text.className = 'perf-bar__text';
    text.textContent = message;

    var action = document.createElement('button');
    action.type = 'button';
    action.className = 'perf-bar__action';
    action.textContent = 'Turn off';
    action.addEventListener('click', function () {
      onTurnOff();
    });

    this.el.appendChild(text);
    this.el.appendChild(action);
    this._reveal();
  };

  // If a bar is already showing, slide it fully away first and only render +
  // slide in the replacement once that's finished — two bars should never
  // instant-swap content or overlap.
  PerfNotificationBar.prototype._swapTo = function (renderFn) {
    this._clearAutoDismiss();

    if (this.el.hidden) {
      renderFn();
      return;
    }

    this.el.classList.remove('is-visible');
    var self = this;
    window.setTimeout(function () {
      self.el.hidden = true;
      renderFn();
    }, this._transitionMs);
  };

  PerfNotificationBar.prototype._reveal = function () {
    this.el.hidden = false;
    void this.el.offsetWidth; // Force reflow so the slide-in transition runs.
    this.el.classList.add('is-visible');
  };

  PerfNotificationBar.prototype.hide = function () {
    this._clearAutoDismiss();
    this.el.classList.remove('is-visible');
    var el = this.el;
    window.setTimeout(function () {
      el.hidden = true;
    }, this._transitionMs);
  };

  PerfNotificationBar.prototype._clearAutoDismiss = function () {
    if (this._autoDismissId) {
      clearTimeout(this._autoDismissId);
      this._autoDismissId = null;
    }
  };

  // --- Controller: ties monitor + bar + body classes + settings together -

  var bar = new PerfNotificationBar();

  function isStored(key) {
    return localStorage.getItem(key) === 'on';
  }

  function setStored(key, on) {
    localStorage.setItem(key, on ? 'on' : 'off');
  }

  function syncCheckbox(name, checked) {
    var input = document.querySelector('[data-perf-toggle="' + name + '"]');
    if (input) input.checked = checked;
  }

  function setFxEnabled(enabled) {
    if (!window.RacingFX) return;
    FX_CATEGORIES.forEach(function (name) {
      window.RacingFX.setEnabled(name, enabled);
    });
  }

  // Keeps the monitor's own tier flags (used only to pick the right
  // threshold and to know when to stop) in sync with the real body state,
  // however that state got set — automatically or from the settings panel.
  function syncMonitorState() {
    monitor.tier1Active = body.classList.contains('high-perf-mode');
    monitor.tier2Active = body.classList.contains('low-ram-mode');
    monitor.belowSince = null;
  }

  // Once the visitor manually turns a tier off, its automatic trigger is
  // retired for the rest of the session — re-checking the box manually
  // re-arms it (see module header comment).
  var tier1UserDismissed = false;
  var tier2UserDismissed = false;

  function maybeStartMonitor() {
    if (tier1UserDismissed) return; // Tier 1's one auto-shot is used up.
    if (tier2UserDismissed && monitor.tier1Active) return; // Tier 1 running, tier 2's auto-shot used up — nothing left to watch for.
    if (monitor.tier2Active) return; // Terminal.
    monitor.start();
  }

  function activateTier1(opts) {
    opts = opts || {};
    tier1UserDismissed = false;
    body.classList.add('high-perf-mode');
    setStored(STORAGE.tier1, true);
    syncCheckbox('high-perf', true);
    if (opts.notify !== false) bar.showTier1(deactivateTier1);
    syncMonitorState();
    maybeStartMonitor();
  }

  function deactivateTier1() {
    tier1UserDismissed = true;
    body.classList.remove('high-perf-mode');
    setStored(STORAGE.tier1, false);
    syncCheckbox('high-perf', false);
    bar.hide();
    syncMonitorState();
    maybeStartMonitor();
  }

  function activateTier2(opts) {
    opts = opts || {};
    tier2UserDismissed = false;
    body.classList.add('low-ram-mode');
    setStored(STORAGE.tier2, true);
    syncCheckbox('low-ram', true);
    setFxEnabled(false); // Wind / glow / sheen / card cleanup — see racing-theme.js.
    if (opts.notify !== false) bar.showTier2(deactivateTier2);
    syncMonitorState();
    monitor.stop(); // Tier 2 active — stop tracking to save cycles.
  }

  function deactivateTier2() {
    tier2UserDismissed = true;
    body.classList.remove('low-ram-mode');
    setStored(STORAGE.tier2, false);
    syncCheckbox('low-ram', false);
    setFxEnabled(true);
    bar.hide();
    syncMonitorState();
    maybeStartMonitor();
  }

  var monitor = new FPSMonitor({
    tier1Threshold: 45,
    tier2Threshold: 25,
    tier1Delay: 2000,
    tier2Delay: 2000,
    onTier1: function () {
      if (tier1UserDismissed) return;
      activateTier1();
    },
    onTier2: function () {
      if (tier2UserDismissed) return;
      activateTier2();
    }
  });

  // Manual settings checkboxes — delegated so this keeps working even if
  // Shopify's theme editor re-renders the header section. The two toggles
  // are fully independent: checking "Low RAM Mode" does not touch
  // "Performance Mode", and vice versa.
  document.addEventListener('change', function (e) {
    var target = e.target;
    if (!target.matches) return;

    if (target.matches('[data-perf-toggle="high-perf"]')) {
      if (target.checked) {
        activateTier1({ notify: false });
      } else {
        deactivateTier1();
      }
    } else if (target.matches('[data-perf-toggle="low-ram"]')) {
      if (target.checked) {
        activateTier2({ notify: false });
      } else {
        deactivateTier2();
      }
    }
  });

  // Keep the checkboxes correct if the theme editor swaps the header section
  // back in after this script has already set the body classes.
  document.addEventListener('shopify:section:load', function () {
    syncCheckbox('high-perf', body.classList.contains('high-perf-mode'));
    syncCheckbox('low-ram', body.classList.contains('low-ram-mode'));
  });

  // The FPS trigger only runs while this tab is visible and focused (see
  // FPSMonitor#_isPageActive/_tick). It stops itself as soon as that stops
  // being true; these listeners are what pick it back up once it's true
  // again — a plain tab switch fires visibilitychange, while alt-tabbing to
  // another app with this tab still frontmost only fires window blur/focus.
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') monitor._resume();
  });
  window.addEventListener('focus', function () {
    monitor._resume();
  });

  // --- Boot: restore a returning visitor's manual choices, else watch FPS -

  var restoredTier1 = isStored(STORAGE.tier1);
  var restoredTier2 = isStored(STORAGE.tier2);

  if (restoredTier1) activateTier1({ notify: false });
  if (restoredTier2) activateTier2({ notify: false }); // Also stops the monitor.
  if (!restoredTier1 && !restoredTier2) maybeStartMonitor();
})();
